import {validationMessages as messages} from '../../forms/messages/validationMessages'
import {
  getValidationMessageKey,
  required,
  validateMaxStringLength,
  ValidationRulesType,
  ValidationRuleType,
  ValidatorType,
  validSpecialCharacters,
  validText
} from './index'

export type ValidationMessageId = (typeof messages)[keyof typeof messages]['id']

export const MAX_NAME_LENGTH = 50

export const customValidationField = (
  validate: ValidatorType,
  messageKey: ValidationMessageId
) => ({
  validate,
  messageKey
})

export const requiredField = (messageKey: ValidationMessageId) =>
  customValidationField(required, messageKey)

/** Rule that fails on characters outside ISO-8859-15 */
export const invalidNonLatinCharactersValidation = customValidationField(
  validText,
  messages.invalidNonLatinCharactersV2.id
)

const invalidSpecialCharactersValidation = customValidationField(
  validSpecialCharacters,
  messages.invalidSpecialCharacters.id
)

const invalidNameLengthValidation = customValidationField(
  validateMaxStringLength(MAX_NAME_LENGTH),
  messages.tooLongName.id
)

/** First-name rules, in the order they run */
export const firstNameValidation: ValidationRuleType = [
  requiredField(messages.emptyFirstNameV2.id),
  invalidNonLatinCharactersValidation,
  invalidSpecialCharactersValidation,
  invalidNameLengthValidation
]

/** Last-name rules, in the order they run */
export const lastNameValidation: ValidationRuleType = [
  requiredField(messages.emptyLastNameV2.id),
  invalidNonLatinCharactersValidation,
  invalidSpecialCharactersValidation,
  invalidNameLengthValidation
]

/** Both name rule sets keyed by field, for `getValidationMessageKey` */
export const nameValidationRules: ValidationRulesType = {
  firstName: firstNameValidation,
  lastName: lastNameValidation
}

export interface GuestNameValidation {
  firstName: ValidationMessageId | null
  lastName: ValidationMessageId | null
  isValid: boolean
}

const getNameMessageKey = getValidationMessageKey(nameValidationRules)

/**
 * Runs the payment form name rules on the raw values, so a whitespace-only name passes.
 * @param firstName - The first name as typed or held by the account
 * @param lastName - The last name as typed or held by the account
 * @returns The first failing message id per field, and whether both pass
 */
export const validateGuestName = (
  firstName: string,
  lastName: string
): GuestNameValidation => {
  const firstNameError = (getNameMessageKey('firstName', firstName) ??
    null) as ValidationMessageId | null
  const lastNameError = (getNameMessageKey('lastName', lastName) ??
    null) as ValidationMessageId | null

  return {
    firstName: firstNameError,
    lastName: lastNameError,
    isValid: !firstNameError && !lastNameError
  }
}
